import React, { useCallback, useEffect } from 'react';
import { Container, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';
import * as deviceDetector from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment/moment';
import { loadLanguageResources } from './i18n/config';

import AlertToasts from 'pages/alerts/components/AlertToasts';
import Routes from './Routes';
import useUTMQueryStrings from '@ui/hooks/useUTMQueryStrings';
import useServerSideEvents from 'hooks/useServerSideEvents';
import { useLanguage } from '@ui/hooks/useLanguage';
import { useUserSelector } from 'hooks/useUser';
import useImpersonatedAsQueryStrings from '@ui/hooks/useImpersonatedAsQueryString';
import ImpersonatedWarning from './components/ImpersonatedWarning';
import { usePropertyFinderHotjar } from 'hooks/usePropertyFinderHotjar';

const AppContainer = styled(Container)`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
`;

const MobileAppWrapper = styled(Row)`
  @media (max-width: 992px) {
  }
`;

function App() {
  useUTMQueryStrings();
  useServerSideEvents();
  usePropertyFinderHotjar();
  const { impersonatedAs, clearImpersonatedAs } = useImpersonatedAsQueryStrings();
  const { isImpersonated } = useUserSelector();
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  // eslint-disable-next-line no-restricted-globals
  const parsedQueryString = queryString.parse(window.location.search);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const onResize = useCallback(({ width }) => {
    if (width <= 834) {
      deviceDetector.isMobile = true;
    } else {
      deviceDetector.isMobile = false;
    }
  }, []);
  const onExistImpersonating = useCallback(() => {
    clearImpersonatedAs();

    window.location.href = '/';
  }, [clearImpersonatedAs]);

  // Load moment locale dynamically to reduce initial bundle size
  useEffect(() => {
    const loadMomentLocale = async () => {
      if (currentLanguage && currentLanguage !== 'en') {
        const localeMap = {
          es: () => import('moment/locale/es'),
          pt: () => import('moment/locale/pt'),
          de: () => import('moment/locale/de'),
          fr: () => import('moment/locale/fr'),
          se: () => import('moment/locale/se'),
          it: () => import('moment/locale/it'),
          fi: () => import('moment/locale/fi'),
          nl: () => import('moment/locale/nl'),
          ro: () => import('moment/locale/ro'),
          pl: () => import('moment/locale/pl'),
          no: () => import('moment/locale/nb'),
          dk: () => import('moment/locale/da')
        };
        if (localeMap[currentLanguage]) {
          await localeMap[currentLanguage]();
        }
      }
      moment.locale(currentLanguage || 'en');
    };
    loadMomentLocale();
  }, [currentLanguage]);

  const { ref } = useResizeDetector({
    handleHeight: false,
    onResize
  });

  useEffect(() => {
    if (parsedQueryString?.lang) {
      setLanguage({
        lang: parsedQueryString?.lang
      });

      delete parsedQueryString.lang;
      const stringifyQueryString = new URLSearchParams(parsedQueryString).toString();

      window.history.pushState(
        parsedQueryString,
        '',
        `${window.location.href.split('?')[0]}?${stringifyQueryString}`
      );
    }
  }, [parsedQueryString, setLanguage]);

  // Load language resources dynamically, then change language
  useEffect(() => {
    const changeLanguage = async () => {
      const lang = language?.lang ?? 'en';
      await loadLanguageResources(lang);
      await i18n.changeLanguage(lang);
    };
    changeLanguage().catch(console.error);
  }, [language, i18n]);

  // Close uchat widget when navigating to property finder pages
  useEffect(() => {
    const isPropertyFinderPage =
      location.pathname.startsWith('/property-finder') ||
      location.pathname.startsWith('/app/property-finder') ||
      location.pathname.startsWith('/embed/property-finder');

    if (isPropertyFinderPage && window.$chatbot) {
      window.$chatbot.toggle('close');
    }
  }, [location.pathname]);

  return (
    <AppContainer fluid id="scrollable" ref={ref}>
      <MobileAppWrapper>
        {isImpersonated ? (
          <ImpersonatedWarning
            onExistImpersonating={onExistImpersonating}
            impersonatedAs={impersonatedAs}
          />
        ) : null}
        <AlertToasts />

        <Container className="text-center mt-2 mb-2 min-h-100">
          <Routes />
        </Container>
      </MobileAppWrapper>
    </AppContainer>
  );
}

export default App;
